/**
 * PichwaiHeritageWedding — Royal Mewar Pichwai & Rajput Palace digital wedding invitation.
 *
 * Registered under:
 *   - 'exp-wedding-pichwai-v1'
 *   - 'exp-wedding-rajasthani-royal-v1'
 *
 * Ensures:
 *   1. Direct preview routes (/preview/exp-wedding-pichwai-v1, /preview/exp-wedding-rajasthani-royal-v1) render cleanly via registry.get(templateId).
 *   2. PublicInvitation.tsx routes here via registry.has(templateId).
 *   3. TemplateRenderer renders it by ID.
 *   4. StepTemplate & DraftPreviewModal can preview it via live SceneExperienceRenderer.
 */

import React from 'react';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

export const META_PICHWAI: TemplateMetadata = {
  id: 'exp-wedding-pichwai-v1',
  name: 'Royal Pichwai Heritage Wedding',
  occasionType: 'WEDDING',
  description:
    'Sacred Mewar Pichwai miniature art with lotus ponds, dancing peacocks, and 24K gold foil Rajasthani royal calligraphy.',
  isPremium: true,
  tags: [
    'cinematic',
    'wedding',
    'pichwai',
    'heritage',
    'mewar',
    'rajasthani',
    'royal',
    'traditional',
    'gold-leaf',
    'premium',
    'scroll-experience',
    'ai-native',
  ],
  colorScheme: { primary: '#D4AF37', accent: '#DC2626', bg: '#170B0E' },
};

export const META_RAJASTHANI: TemplateMetadata = {
  id: 'exp-wedding-rajasthani-royal-v1',
  name: 'Rajasthani Rajput Royal',
  occasionType: 'WEDDING',
  description:
    'An AI-generated cinematic experience with a grand Rajasthani palace aesthetic. Rich crimson, warm gold, and traditional Rajput ornaments.',
  isPremium: true,
  tags: [
    'cinematic',
    'wedding',
    'ai-native',
    'rajasthani',
    'rajput',
    'royal',
    'heritage',
    'premium',
    'scroll-experience',
  ],
  colorScheme: { primary: '#C84B31', accent: '#D4AF37', bg: '#1C0A00' },
};

export function PichwaiHeritageWedding({ data, preview }: { data: InvitationData; preview?: boolean }) {
  const manifest =
    (data as any)?.sceneManifest ||
    resolveExperienceManifest(data.templateId || 'exp-wedding-pichwai-v1', data);
  return <SceneExperienceRenderer manifest={manifest} preview={preview} />;
}

export function RajasthaniRoyalWedding({ data, preview }: { data: InvitationData; preview?: boolean }) {
  const manifest =
    (data as any)?.sceneManifest ||
    resolveExperienceManifest(data.templateId || 'exp-wedding-rajasthani-royal-v1', data);
  return <SceneExperienceRenderer manifest={manifest} preview={preview} />;
}

registry.register({ metadata: META_PICHWAI, component: PichwaiHeritageWedding });
registry.register({ metadata: META_RAJASTHANI, component: RajasthaniRoyalWedding });
